"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import { blog12 } from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

export default function Blog12() {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Fill Smarter, Save Bigger: How AI Autofill Puts Money Back in Students’ Pockets"
                  date="September 25 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog12}
                alt="Fill Smarter, Save Bigger feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={12} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          In college, every minute counts—and every dollar matters. Between tuition, textbooks, housing, and the occasional caffeine fix, students are constantly juggling time and money. That’s where AI-powered autofill tools like Adobe’s Form Fill come in—not just as a convenience, but as a quiet financial ally.        </IntroParagraph>
        <IntroParagraph>
          AI Autofill isn’t just about speeding up paperwork. It’s about unlocking real monetary benefits by reducing errors, saving time, and helping students seize opportunities they might otherwise miss.        </IntroParagraph>
      </IntroSection>

      <ContentSection title="Time Saved = Money Earned">
        <ContentParagraph>
          Think about how many forms students fill out each semester: financial aid, scholarship applications, housing requests, internship paperwork, course registration, and more. AI Autofill can complete these forms in seconds by recognizing patterns, remembering previous entries, and intelligently suggesting responses. That’s hours saved—hours that can be spent working a part-time job, studying for a scholarship exam, or applying for paid opportunities.
        </ContentParagraph>
        <ContentParagraph>
          For a student earning $20/hour tutoring, saving just 5 hours a month on admin tasks translates to $100 in potential earnings. Over a semester, that’s hundreds of dollars simply by letting AI handle the busywork.          </ContentParagraph>
      </ContentSection>

      <ContentSection title="Fewer Mistakes, Faster Money">
        <ContentParagraph>
          Mistakes on forms can be costly. A missed field on a FAFSA application could delay financial aid. An error on a reimbursement form might mean waiting weeks for funds. AI Autofill reduces these risks by guiding students through forms, flagging missing information, and ensuring accuracy.          </ContentParagraph>
        <ContentParagraph>
          Fewer errors mean faster approvals—and faster access to money students need for tuition, rent, or groceries.          
        </ContentParagraph>
      </ContentSection>

      <ContentSection title="More Opportunities, Less Friction">
        <ContentParagraph>
          AI Autofill lowers the barrier to applying for scholarships, grants, and internships. When forms are easier to complete, students are more likely to apply. That means more chances to win funding, land paid positions, or secure travel stipends.
        </ContentParagraph>
        <ContentParagraph>
          It’s simple math: more applications submitted = higher odds of financial gain.
        </ContentParagraph>
        <ContentParagraph>
          In a world where students are stretched thin, AI Autofill offers more than convenience—it offers leverage. 
          It helps students reclaim time, avoid costly mistakes, and pursue financial opportunities with confidence. 
          So the next time you open a form, let AI do the heavy lifting—and watch how those small efficiencies add up to big savings.
        </ContentParagraph>
      </ContentSection>

    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;
